import Backbone from 'backbone'
import template from './swap.hbs'
import _ from 'lodash'
import Breadcrumbs from 'scoreboard/components/breadcrumbs'
import $ from 'jquery'
import ObjectSelectorForm from 'scoreboard/components/object-selector-form'
import i18n from 'scoreboard/components/i18n'
import appDisplay from 'scoreboard/components/app-display'
import globals from 'scoreboard/components/globals'
import highlight from 'scoreboard/components/highlight'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import StrategyMapObjectSelectorForm from './strategy-map-object-selector-form'
import metadata from 'scoreboard/components/metadata'
import projects from 'scoreboard/projects/projects'
import permissions from 'scoreboard/components/permissions'
import scrollContainer from 'scoreboard/components/scroll-container'

var Swap = Backbone.View.extend({

	// this.model is a Dashboard

	className: 'swap',

	events: {
		'click .panel-header a': 'orgBreadcrumbs',
		'click .panel-content a': 'scorecardBreadcrumbs',
		'click .panel-content li[data-id]': 'swap',
		'click .done': 'remove'
	},

	initialize: function(options) {
		this.options = options || {}
		this.dirty = false
		if (this.model) { // this.model may be missing in unit tests
			this._setData(this.model.data.get())
		}
	},

	render: function() {
		var context = this.context(this.data, globals.periodId)
		var noData = _.isEmpty(_.get(this.data, 'scorecards'))

		this.$el.empty().append(template(_.extend(context, {
			noData: noData
		})))
		if (noData) {
			// If there's no data, mark the dialog as headerless
			// and populate the message
			this.$el.addClass('dialog-message')
		}
		// Initialize the tooltips
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })
		return this
	},

	/**
	 * Creates the context, which is an array of orgs with scorecard items
	 * tucked underneath them.  For example:
	 *
	 * <pre>
	 * [
	 *     {
	 *         id: 2,
	 *         label: "another org",
	 *         scorecardItems: [ { id: 20, organizationId: 2, label: "sn C in another org" } ]
	 *     },
	 *     {
	 *         id: 1,
	 *         label: "org one",
	 *         scorecardItems: [
	 *             { id: 10, organizationId: 1, label: "sn A in org one" },
	 *             { id: 11, organizationId: 1, label: "sn B in org one" }
	 *         ]
	 *     }
	 * ]
 	 * </pre>
 	 *
 	 * Allows explicitly passing in the data for testing because the Dashboard
	 * model automatically attaches "scores" properties to the scorecard items
	 * and we don't want this muddying the test
	 */
	context: function(data, periodId) {
		if (!data) {
			return null
		}

		var orgsWithScorecardNodes = _(data.scorecards)
			.map(function (sn) { return sn.organizationId })
			.uniq().value()

		var orgs = _.chain(data.organizations)
			.values() // convert orgs to a simple array, not keyed by ID
			.filter(function (org) {
				// #11234 only include orgs that actually have scorecard nodes in this dashboard
				return _.includes(orgsWithScorecardNodes, org.id)
			})
			.filter(function (org) {
				// #11239 only include orgs the user can view
				return org.view
			})
			.clone()
			.sortBy(function(org) {
				return org.label.toLowerCase()
			})
			.each(function(org) {
				org.scorecardItems = []
			})
			.value()

		// tuck scorecard items under orgs
		var orgsById = _.keyBy(orgs, 'id')
		_.each(data.scorecards, function(scorecardItem) {
			var org = orgsById[scorecardItem.organizationId]
			if (!org) {
				// if the scorecard node's org can't be found (eg no permission to view the org), then skip it
				return
			}

			var simpleItem = _.pick(scorecardItem, 'id', 'type', 'label')
			var color = 'gray'
			if (scorecardItem.scores) {
				var scoresById = _.keyBy(scorecardItem.scores, 'periodId')
				if (scoresById[periodId]) {
					color = scoresById[periodId].color
				}
			}
			simpleItem.color = color || 'gray'
			org.scorecardItems.push(simpleItem)
		})

		// sort the scorecard items in each org
		_.each(orgs, function(org) {
			org.scorecardItems = _.sortBy(org.scorecardItems, function(item) {
				return item.label.toLowerCase()
			})
		})

		return {
			organizations: orgs,
			sectionSingular: i18n.get(metadata[this.model.get('section')].singularCode)
				.toLowerCase()
		}
	},

	orgBreadcrumbs: function(e) {
		this.breadcrumbs(e, 'organizations')
	},

	scorecardBreadcrumbs: function(e) {
		this.breadcrumbs(e, 'scorecards')
	},

	breadcrumbs: function(e, section) {
		_.invokeMap([ this.breadcrumbs ], 'remove')
		let target = this.$(e.target).closest('[data-id]')
		let id = target.data('id')
		let type = target.data('type')
		var url = 'api/' + section + '/' + id + '/ancestors'

		$.get(url, ancestors => {
			// Display the project before the organization name if we're displaying
			// an org breadcrumb since these orgs can be from different projects.
			if (type == 'organization' && permissions.modifyProjects()) {
				let projectId = this._getProjectId(id)
				ancestors = [{
					id: projectId,
					label: projects.get(projectId).get('label')
				}, ...ancestors]
			}

			this.breadcrumbsTooltip = Breadcrumbs.tooltip({
				target: e.target,
				objects: ancestors
			})
		})
	},

	/**
	 * Combine the passed in scorecard and org data with the data from each chart
	 * @private
	 */
	_setData: function(data) {
		let scorecards = data.scorecards
		let organizations = data.organizations
		_.forEach(data.chartData, d => {
			scorecards = _.extend(scorecards || {}, d.chartData.scorecards)
			organizations = _.extend(organizations || {}, d.chartData.organizations)
		})

		this.data = { scorecards, organizations }
	},

	swap: function(e) {
		// make sure we don't invoke the swap functionality when clicking on the
		// info link
		if (!this.$(e.target).closest('ul').hasClass('list')) {
			return
		}

		// find the object the user clicked on
		var id = this.$(e.target).closest('[data-id]').data('id')
		var object = this.data.scorecards[id]
		var projectId = this._getProjectId(object.organizationId)

		// #16058 for strategy maps, use a object selector form
		// that only permits replacing perspectives and objectives with
		// the same item type
		let selectorArgs = {
			title: i18n.get('dashboards.swap.dialog.replacement.title'),
			orgId: object.organizationId,
			section: 'scorecards',
			banner: this.banner(object),
			type: object.type,
			primaryButtonKey: 'dashboards.swap.primaryButtonKey',
			showProjectSelector: true,
			projectId: projectId || projects.id
		}
		var objectSelectorForm
		if (this.model.get('section') === 'dashboards') {
			objectSelectorForm = new ObjectSelectorForm(selectorArgs)
		} else {
			objectSelectorForm = new StrategyMapObjectSelectorForm(selectorArgs)
		}

		objectSelectorForm.on('select', function(node) {
			objectSelectorForm.trigger('done')
			var url = 'api/dashboards/' + this.model.id + '/swap' +
				'?oldId=' + id +
				'&newId=' + node.id +
				'&calendarPeriodId=' + globals.periodId
			$.post(url, function(data) {
				this._setData(data)
				var scrollPosition = scrollContainer(this.el).scrollTop()
				this.render()
				scrollContainer(this.el).scrollTop(scrollPosition)
			}.bind(this))
			this.dirty = true
		}.bind(this))
		appDisplay.dialog(objectSelectorForm)
	},

	_getProjectId(orgId) {
		return _.get(this.data.organizations[orgId], 'projectId')
	},

	banner: function (object) {
		let label = highlight(object.label)
		let key = 'dashboards.swap.selectNode'
		if (this.model.get('section') === 'strategyMaps' && (object.type === 'perspective' || object.type === 'objective')) {
			key = 'dashboards.swap.select.' + object.type
		}
		return i18n.get(key, label)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([ this.breadcrumbsTooltip, this.covertButtonTooltips ], 'remove')
		if (this.dirty) {
			this.trigger('swap')
		}
	}

})

export default Swap

Swap.__filename__ = 'dashboards/edit/swap.js'
